import { navigate } from "raviger";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

import { Avatar } from "@/components/Common/Avatar";

import { formatEuroCents, formatName } from "@/Utils/utils";
import { SpecialtyInfo } from "@/types/scheduling/PublicAppointmentApi";
import { storeUserInLocalStorage } from "@/types/scheduling/schedule";
import { UserReadMinimal } from "@/types/user/user";

interface Props {
  user: UserReadMinimal;
  className?: string;
  facilityId: string;
  /** Konsultacijos kaina centais (per specialistą). null → nerodom. */
  feeCents?: number | null;
  specialties?: SpecialtyInfo[];
  skills?: SpecialtyInfo[];
}

export function UserCard({
  user,
  className,
  facilityId,
  feeCents,
  specialties = [],
  skills = [],
}: Props) {
  const { t } = useTranslation();
  const feeLabel = formatEuroCents(feeCents);

  // Visada vedam į laikų peržiūrą — laikai rodomi VIEŠAI (be prisijungimo);
  // prisijungti prašom tik rezervuojant pasirinktą laiką.
  const returnLink = useMemo(
    () => `/facility/${facilityId}/appointments/${user.id}/book-appointment`,
    [facilityId, user.id],
  );

  return (
    <Card className={cn("overflow-hidden bg-white", className)}>
      <div className="flex flex-col justify-between h-full">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <Avatar
              imageUrl={user.profile_picture_url}
              name={formatName(user, true)}
              className="size-32 rounded-lg"
            />

            <div className="flex grow flex-col min-w-0">
              <h3 className="truncate text-xl font-semibold">
                {formatName(user)}
              </h3>
              <p className="text-sm text-gray-500">{user.user_type}</p>
              {specialties.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {specialties.map((sp) => (
                    <Badge key={sp.key} variant="primary">
                      {sp.name_lt}
                    </Badge>
                  ))}
                </div>
              )}
              {skills.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {skills.map((sk) => (
                    <Badge key={sk.key} variant="secondary">
                      {sk.name_lt}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-auto border-t border-gray-100 bg-gray-50 p-4">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-y-2">
            {feeLabel && (
              <div className="flex flex-col">
                <span className="text-xs text-gray-500">
                  {t("consultation_price")}
                </span>
                <span className="text-lg font-semibold">{feeLabel}</span>
              </div>
            )}
            <Button
              variant="outline"
              onClick={() => {
                storeUserInLocalStorage(user);
                navigate(returnLink);
              }}
            >
              {t("book_appointment")}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
